import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, createBlock as _createBlock, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "with-side-panel" }
const _hoisted_2 = { class: "content-i" }
const _hoisted_3 = { class: "content-box" }
const _hoisted_4 = { class: "element-wrapper" }
const _hoisted_5 = { class: "d-flex justify-content-between" }
const _hoisted_6 = { class: "element-header" }
const _hoisted_7 = { class: "element-box" }
const _hoisted_8 = { class: "row" }
const _hoisted_9 = { class: "col-lg-6" }
const _hoisted_10 = { class: "row" }
const _hoisted_11 = { class: "col-6" }
const _hoisted_12 = { class: "col-6" }
const _hoisted_13 = { key: 0 }
const _hoisted_14 = { key: 1 }
const _hoisted_15 = { class: "col-lg-6" }
const _hoisted_16 = { key: 0 }
const _hoisted_17 = {
  key: 0,
  class: "row mt-3"
}
const _hoisted_18 = { class: "col-12" }
const _hoisted_19 = { class: "mb-3" }
const _hoisted_20 = { class: "col-lg-6" }
const _hoisted_21 = {
  key: 0,
  class: "d-block text-muted mb-1"
}
const _hoisted_22 = {
  key: 1,
  class: "d-block text-muted mb-1"
}
const _hoisted_23 = { class: "col-lg-6" }
const _hoisted_24 = { class: "form-buttons-w text-center" }
const _hoisted_25 = ["disabled", "title"]
const _hoisted_26 = ["disabled"]

import officeService from '@/api/Office';
import BaseCheckbox from '@/components/base/BaseCheckbox.vue';
import BaseInput from '@/components/base/BaseInput.vue';
import CreateButton from '@/components/CreateButton.vue';
import Sidebar from '@/components/FormSidebar.vue';
import ModalConfirm from '@/components/modal/ModalConfirm.vue';
import Datepicker from '@/components/picker/Datepicker.vue';
import SelectedDropdown from '@/components/SelectDropdown.vue';
import {CloseToast, ErrorToast, SuccessToast} from '@/components/VueToast';
import router from '@/router';
import {isValidEmail, requiredRule} from '@/shared/validators/validationRules';
import {piniaEnumsStore} from '@/store/piniaEnumsStore';
import {piniaCompanySettingsStore} from '@/store/piniaCompanySettingsStore';
import useVuelidate from '@vuelidate/core';
import {computed, onMounted, ref, reactive} from 'vue';
import {useI18n} from 'vue-i18n';
import {useRoute} from 'vue-router';
import {IOffice, IRegion, IRegionFromFederalState} from "@/interfaces/Office/IOffice";
import BackButton from "@/components/button/BackButton.vue";
import SaveButton from "@/components/button/SaveButton.vue";


export default /*@__PURE__*/_defineComponent({
  __name: 'Form',
  setup(__props) {

const {t} = useI18n();
const piniaEnumsState = piniaEnumsStore();
const getEnums = computed(() => piniaEnumsState.enums);
const federalStatesDropdown = ref([]);
const officeEngagementTypesDropdown = ref([]);
const officeTypesDropdown = ref([]);
const regionsDropdown = ref<IRegion[]>([]);
const isSubmitting = ref(false);
const isLoaded = ref(false);
const showStatusModal = ref(false);
const statusAction = ref<'activate' | 'close' | null>(null);

// Office approval workflow (status, checklist, activate/close) is BSG only, switched on by backend env
const piniaCompanySettingsState = piniaCompanySettingsStore();
const isApprovalWorkflowEnabled = computed(() => !!piniaCompanySettingsState.companySettings?.officeApprovalWorkflowEnabled);
const route = useRoute();
const officeId = route.params.id;

const formData = reactive<IOffice>({
  name: '',
  address: '',
  houseNumber: '',
  zip: '',
  engagementType: '',
  colorCode: '#43ad4e',
  city: '',
  phone: '',
  email: '',
  type: '',
  federalState: '',
  region: '',
  regionTranslation: '',
  costCenter: '',
  approvedOn: null,
  closedAt: null,
  contractsInPlace: false,
  floorPlanAvailable: false,
  photosAvailable: false,
  checklistAvailable: false,
  submittedToCertquaOnDate: null,
});

const statusBadgeClass = computed(() => {
  switch (formData.status) {
    case 'active':
      return 'text-bg-success';
    case 'closed':
      return 'text-bg-danger';
    default:
      return 'text-bg-warning';
  }
});

const rules = computed(() => {
  return {
    name: requiredRule(t),
    address: requiredRule(t),
    houseNumber: requiredRule(t),
    zip: requiredRule(t),
    engagementType: requiredRule(t),
    type: requiredRule(t),
    city: requiredRule(t),
    federalState: requiredRule(t),
    region: requiredRule(t),
    costCenter: requiredRule(t),
    email: isValidEmail(t),
  };
});

const v$ = useVuelidate<IOffice>(rules, formData);

async function submitForm() {
  v$.value.$touch();
  if (v$.value.$invalid) {
    return;
  }

  if (isSubmitting.value) return;
  isSubmitting.value = true;

  try {
    if (officeId) {
      await officeService.putOffice(formData, +officeId);
    } else {
      await officeService.postOffice(formData);
    }

    await router.push({name: 'officeList'});
    isSubmitting.value = false;
    SuccessToast(t('general.toasts.save_success'));
  } catch (error) {
    await ErrorToast(error);
  } finally {
    isSubmitting.value = false;
  }
}

function openStatusModal(action: 'activate' | 'close') {
  CloseToast();
  v$.value.$touch();
  if (v$.value.$invalid) {
    return;
  }

  statusAction.value = action;
  showStatusModal.value = true;
}

// Save first, so the entered approval/closing date is stored before the status check on backend
async function confirmStatusChange() {
  showStatusModal.value = false;

  if (isSubmitting.value) return;
  isSubmitting.value = true;

  try {
    await officeService.putOffice(formData, +officeId);

    if (statusAction.value === 'activate') {
      await officeService.activateOffice(+officeId);
    } else if (statusAction.value === 'close') {
      await officeService.closeOffice(+officeId);
    }

    await getOffice();
    SuccessToast(t('general.toasts.save_success'));
  } catch (error) {
    await ErrorToast(error);
  } finally {
    isSubmitting.value = false;
    statusAction.value = null;
  }
}

async function getOffice() {
  try {
    const {data} = await officeService.getOffice(+officeId);
    const regionTranslation = data.data.regionTranslation

    Object.assign(formData, {
      ...data.data,
      regionTranslation: data.data.regionTranslation
    });
  } catch (error) {
    await ErrorToast(error);
  }
}

const updateFederalState = (newValue: string) => {
  // const regionFromFederalState = regionsDropdown.value.find(
  //     regionDropdown => regionDropdown.federal_state_key === newValue
  // );

  const regionFromFederalState = regionsDropdown.value.find(
      regionDropdown => regionDropdown.federal_state_key === newValue
  ) as IRegionFromFederalState | undefined;

  if (regionFromFederalState) {
    formData.region = regionFromFederalState.key;
    formData.regionTranslation = regionFromFederalState.value;
  } else {
    formData.region = '';
    formData.regionTranslation = '';
  }
};

async function getOfficeEnumDropdowns() {
  federalStatesDropdown.value = getEnums.value?.federalStates || [];
  officeTypesDropdown.value = getEnums.value?.officeTypes || [];
  officeEngagementTypesDropdown.value = getEnums.value?.officeEngagementTypes || [];
  regionsDropdown.value = getEnums.value?.regions || [];
}

onMounted(async () => {
  //if (Object.keys(getEnums.value).length === 0) {
  //  await store.dispatch('getEnums', { t });
  //}

  await getOfficeEnumDropdowns();
  if (officeId) {
    await getOffice();
  }

  isLoaded.value = true;
});


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", _hoisted_4, [
          _createVNode(BackButton, {
            label: _ctx.$t('general.button.back'),
            to: { name: 'officeList' },
            marginBottom: "5"
          }, null, 8, ["label"]),
          _createElementVNode("div", _hoisted_5, [
            _createElementVNode("h6", _hoisted_6, _toDisplayString(_ctx.$t('office.title')), 1)
          ]),
          _createElementVNode("div", _hoisted_7, [
            (isLoaded.value)
              ? (_openBlock(), _createElementBlock("form", {
                  key: 0,
                  onSubmit: _withModifiers(submitForm, ["prevent"]),
                  novalidate: ""
                }, [
                  _createElementVNode("div", _hoisted_8, [
                    _createElementVNode("div", _hoisted_9, [
                      _createVNode(BaseInput, {
                        modelValue: formData.name,
                        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((formData.name) = $event)),
                        label: "Bezeichnung",
                        disabled: formData.hubspotId,
                        required: true,
                        validation: _unref(v$).name,
                        messages: _unref(v$).name.required.$message
                      }, null, 8, ["modelValue", "disabled", "validation", "messages"]),
                      _createVNode(BaseInput, {
                        modelValue: formData.address,
                        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((formData.address) = $event)),
                        label: "Straße",
                        disabled: formData.hubspotId,
                        required: true,
                        validation: _unref(v$).address,
                        messages: _unref(v$).address.required.$message
                      }, null, 8, ["modelValue", "disabled", "validation", "messages"]),
                      _createElementVNode("div", _hoisted_10, [
                        _createElementVNode("div", _hoisted_11, [
                          _createVNode(BaseInput, {
                            modelValue: formData.houseNumber,
                            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((formData.houseNumber) = $event)),
                            label: "Hausnummer",
                            disabled: formData.hubspotId,
                            required: true,
                            validation: _unref(v$).houseNumber,
                            messages: _unref(v$).houseNumber.required.$message
                          }, null, 8, ["modelValue", "disabled", "validation", "messages"])
                        ]),
                        _createElementVNode("div", _hoisted_12, [
                          _createVNode(BaseInput, {
                            modelValue: formData.zip,
                            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((formData.zip) = $event)),
                            label: "PLZ",
                            disabled: formData.hubspotId,
                            required: true,
                            validation: _unref(v$).zip,
                            messages: _unref(v$).zip.required.$message
                          }, null, 8, ["modelValue", "disabled", "validation", "messages"])
                        ])
                      ]),
                      (officeEngagementTypesDropdown.value && officeEngagementTypesDropdown.value.length)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                            _createVNode(SelectedDropdown, {
                              modelValue: formData.engagementType,
                              "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((formData.engagementType) = $event)),
                              options: officeEngagementTypesDropdown.value,
                              disabled: formData.hubspotId,
                              required: true,
                              label: _ctx.$t('general.office_engagement_type'),
                              placeholder: _ctx.$t('placeholder.engagement_type'),
                              validation: _unref(v$).engagementType,
                              messages: _unref(v$).engagementType.required.$message
                            }, null, 8, ["modelValue", "options", "disabled", "label", "placeholder", "validation", "messages"])
                          ]))
                        : _createCommentVNode("", true),
                      (officeTypesDropdown.value && officeTypesDropdown.value.length)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                            _createVNode(SelectedDropdown, {
                              modelValue: formData.type,
                              "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((formData.type) = $event)),
                              options: officeTypesDropdown.value,
                              disabled: formData.hubspotId,
                              required: true,
                              label: _ctx.$t('general.type'),
                              placeholder: _ctx.$t('placeholder.type'),
                              validation: _unref(v$).type,
                              messages: _unref(v$).type.required.$message
                            }, null, 8, ["modelValue", "options", "disabled", "label", "placeholder", "validation", "messages"])
                          ]))
                        : _createCommentVNode("", true),
                      _createVNode(BaseInput, {
                        modelValue: formData.colorCode,
                        "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((formData.colorCode) = $event)),
                        label: "Standortfarbe",
                        required: true,
                        style: {"width":"100px","float":"left"},
                        type: "color",
                        inputStyle: {
                               width: '50px',
                               backgroundColor: '#f0f0f0',
                               padding: '1px',
                             }
                      }, null, 8, ["modelValue"])
                    ]),
                    _createElementVNode("div", _hoisted_15, [
                      _createVNode(BaseInput, {
                        modelValue: formData.city,
                        "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((formData.city) = $event)),
                        label: "Stadt",
                        disabled: formData.hubspotId,
                        required: true,
                        validation: _unref(v$).city,
                        messages: _unref(v$).city.required.$message
                      }, null, 8, ["modelValue", "disabled", "validation", "messages"]),
                      _createVNode(BaseInput, {
                        modelValue: formData.phone,
                        "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((formData.phone) = $event)),
                        disabled: formData.hubspotId,
                        label: "Telefonnummer"
                      }, null, 8, ["modelValue", "disabled"]),
                      _createVNode(BaseInput, {
                        modelValue: formData.email,
                        "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((formData.email) = $event)),
                        label: "Email",
                        type: "email",
                        disabled: formData.hubspotId,
                        validation: _unref(v$).email,
                        messages: _unref(v$).email.isValidEmail.$message
                      }, null, 8, ["modelValue", "disabled", "validation", "messages"]),
                      (federalStatesDropdown.value && federalStatesDropdown.value.length)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
                            _createVNode(SelectedDropdown, {
                              modelValue: formData.federalState,
                              "onUpdate:modelValue": [
                                _cache[10] || (_cache[10] = ($event: any) => ((formData.federalState) = $event)),
                                updateFederalState
                              ],
                              options: federalStatesDropdown.value,
                              required: true,
                              disabled: formData.hubspotId,
                              label: _ctx.$t('general.federal_state'),
                              placeholder: _ctx.$t('placeholder.select_federal_state'),
                              validation: _unref(v$).federalState,
                              messages: _unref(v$).federalState.required.$message
                            }, null, 8, ["modelValue", "options", "disabled", "label", "placeholder", "validation", "messages"])
                          ]))
                        : _createCommentVNode("", true),
                      _createVNode(BaseInput, {
                        modelValue: formData.regionTranslation,
                        "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((formData.regionTranslation) = $event)),
                        label: "Region",
                        required: true,
                        validation: _unref(v$).region,
                        disabled: formData.hubspotId,
                        messages: _unref(v$).region.required.$message,
                        readonly: ""
                      }, null, 8, ["modelValue", "validation", "disabled", "messages"]),
                      _createVNode(BaseInput, {
                        modelValue: formData.costCenter,
                        "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event: any) => ((formData.costCenter) = $event)),
                        label: "KS1",
                        required: true,
                        validation: _unref(v$).costCenter,
                        messages: _unref(v$).costCenter.required.$message
                      }, null, 8, ["modelValue", "validation", "messages"])
                    ])
                  ]),
                  (isApprovalWorkflowEnabled.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_17, [
                        _createElementVNode("div", _hoisted_18, [
                          _createElementVNode("h6", _hoisted_19, [
                            _cache[23] || (_cache[23] = _createTextVNode(" Status & Freigabe ", -1)),
                            (_unref(officeId) && formData.status)
                              ? (_openBlock(), _createElementBlock("span", {
                                  key: 0,
                                  class: _normalizeClass(["badge ms-2 px-2 py-1 align-middle", statusBadgeClass.value])
                                }, _toDisplayString(formData.statusTranslation), 3))
                              : _createCommentVNode("", true)
                          ])
                        ]),
                        _createElementVNode("div", _hoisted_20, [
                          (isApprovalWorkflowEnabled.value && _unref(officeId) && formData.status === 'pending')
                            ? (_openBlock(), _createElementBlock("small", _hoisted_21, [...(_cache[24] || (_cache[24] = [
                                _createElementVNode("i", { class: "fa-solid fa-circle-info me-1" }, null, -1),
                                _createTextVNode("Für die Aktivierung muss „Zugelassen am“ eingetragen sein. ", -1)
                              ]))]))
                            : _createCommentVNode("", true),
                          _createVNode(Datepicker, {
                            modelValue: formData.approvedOn,
                            "onUpdate:modelValue": _cache[13] || (_cache[13] = ($event: any) => ((formData.approvedOn) = $event)),
                            label: "Zugelassen am",
                            position: "up"
                          }, null, 8, ["modelValue"]),
                          (_unref(officeId) && formData.status !== 'pending')
                            ? (_openBlock(), _createElementBlock("small", _hoisted_22, [...(_cache[25] || (_cache[25] = [
                                _createElementVNode("i", { class: "fa-solid fa-circle-info me-1" }, null, -1),
                                _createTextVNode("Datum, ab dem der Standort abgemeldet ist. 7 Tage danach wird er automatisch gelöscht. ", -1)
                              ]))]))
                            : _createCommentVNode("", true),
                          (_unref(officeId) && formData.status !== 'pending')
                            ? (_openBlock(), _createBlock(Datepicker, {
                                key: 2,
                                modelValue: formData.closedAt,
                                "onUpdate:modelValue": _cache[14] || (_cache[14] = ($event: any) => ((formData.closedAt) = $event)),
                                label: "Abgemeldet zum",
                                position: "up"
                              }, null, 8, ["modelValue"]))
                            : _createCommentVNode("", true),
                          _createVNode(Datepicker, {
                            modelValue: formData.submittedToCertquaOnDate,
                            "onUpdate:modelValue": _cache[15] || (_cache[15] = ($event: any) => ((formData.submittedToCertquaOnDate) = $event)),
                            label: "Eingereicht bei Certqua am",
                            position: "up"
                          }, null, 8, ["modelValue"])
                        ]),
                        _createElementVNode("div", _hoisted_23, [
                          _createVNode(BaseCheckbox, {
                            modelValue: formData.contractsInPlace,
                            "onUpdate:modelValue": _cache[16] || (_cache[16] = ($event: any) => ((formData.contractsInPlace) = $event)),
                            label: "Verträge liegen vor",
                            labelPosition: "right"
                          }, null, 8, ["modelValue"]),
                          _createVNode(BaseCheckbox, {
                            modelValue: formData.floorPlanAvailable,
                            "onUpdate:modelValue": _cache[17] || (_cache[17] = ($event: any) => ((formData.floorPlanAvailable) = $event)),
                            label: "Grundriss liegt vor",
                            labelPosition: "right"
                          }, null, 8, ["modelValue"]),
                          _createVNode(BaseCheckbox, {
                            modelValue: formData.photosAvailable,
                            "onUpdate:modelValue": _cache[18] || (_cache[18] = ($event: any) => ((formData.photosAvailable) = $event)),
                            label: "Fotos liegen vor",
                            labelPosition: "right"
                          }, null, 8, ["modelValue"]),
                          _createVNode(BaseCheckbox, {
                            modelValue: formData.checklistAvailable,
                            "onUpdate:modelValue": _cache[19] || (_cache[19] = ($event: any) => ((formData.checklistAvailable) = $event)),
                            label: "Checkliste liegt vor",
                            labelPosition: "right"
                          }, null, 8, ["modelValue"])
                        ])
                      ]))
                    : _createCommentVNode("", true),
                  _createElementVNode("div", _hoisted_24, [
                    _createVNode(SaveButton, {
                      label: _ctx.$t('general.button.save'),
                      loading: isSubmitting.value
                    }, null, 8, ["label", "loading"]),
                    (isApprovalWorkflowEnabled.value && _unref(officeId) && formData.status === 'pending')
                      ? (_openBlock(), _createElementBlock("button", {
                          key: 0,
                          type: "button",
                          class: "status-button status-button-activate ms-2",
                          disabled: isSubmitting.value || !formData.approvedOn,
                          title: !formData.approvedOn ? 'Bitte zuerst „Zugelassen am“ eintragen' : '',
                          onClick: _cache[20] || (_cache[20] = ($event: any) => (openStatusModal('activate')))
                        }, [...(_cache[26] || (_cache[26] = [
                          _createElementVNode("i", { class: "fa-solid fa-circle-check" }, null, -1),
                          _createElementVNode("span", null, "Speichern und aktivieren", -1)
                        ]))], 8, _hoisted_25))
                      : _createCommentVNode("", true),
                    (isApprovalWorkflowEnabled.value && _unref(officeId) && formData.status === 'active' && formData.closedAt)
                      ? (_openBlock(), _createElementBlock("button", {
                          key: 1,
                          type: "button",
                          class: "status-button status-button-close ms-2",
                          disabled: isSubmitting.value,
                          onClick: _cache[21] || (_cache[21] = ($event: any) => (openStatusModal('close')))
                        }, [...(_cache[27] || (_cache[27] = [
                          _createElementVNode("i", { class: "fa-solid fa-circle-xmark" }, null, -1),
                          _createElementVNode("span", null, "Speichern und abgemeldet zum", -1)
                        ]))], 8, _hoisted_26))
                      : _createCommentVNode("", true)
                  ])
                ], 32))
              : _createCommentVNode("", true)
          ])
        ])
      ]),
      _createVNode(Sidebar)
    ]),
    _createVNode(ModalConfirm, {
      modelValue: showStatusModal.value,
      "onUpdate:modelValue": _cache[22] || (_cache[22] = ($event: any) => ((showStatusModal).value = $event)),
      header: statusAction.value === 'activate' ? 'Standort aktivieren' : 'Standort abmelden',
      confirmationQuestion: statusAction.value === 'activate'
          ? `Möchten Sie den Standort ${formData.name} speichern und aktivieren?`
          : `Möchten Sie den Standort ${formData.name} speichern und zum ${formData.closedAt} abmelden?`,
      onConfirm: confirmStatusChange
    }, null, 8, ["modelValue", "header", "confirmationQuestion"])
  ]))
}
}

})